import React, { useState } from "react";
import { format } from "date-fns";
import PropTypes from "prop-types";

import TableToolbar from "./KeywordsTableToolbar";
import TableHead from "./KeywordsTableHead";
import EditKeywordModal from "../modals/EditKeywordModal";
import ListEventsModal from "../modals/ListEventsModal";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Paper,
  Checkbox,
  Tooltip,
  IconButton,
} from "@mui/material";

import { Edit } from "@material-ui/icons";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const initialKeywordToEdit = {
  keyword: "",
  keywordID: "",
};

const KeywordsTable = ({
  keywords,
  actions,
  credentials,
  meta,
  keywordRequestBody,
  setKeywordRequestBody,
  updateKeywordState,
  emitSnack,
  slimKeywords,
}) => {
  const { page, rowsPerPage, listEvents } = keywordRequestBody;

  const [order, setOrder] = useState("desc"); // either desc or asc
  const [orderBy, setOrderBy] = useState("dateModified");
  const [selected, setSelected] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [keywordToEdit, setKeywordToEdit] = useState(initialKeywordToEdit);

  const totalRows = meta.count;

  const toggleEditKeywordModal = () => setEditModalOpen(!editModalOpen);

  const handleEditKeyword = (updatedKeyword) => {
    toggleEditKeywordModal();

    actions.edit({ ...updatedKeyword, userID: credentials.userID }, (err) => {
      if (err) {
        return emitSnack({
          type: "error",
          message: "Couldn't Update Keyword",
        });
      }

      emitSnack({
        type: "success",
        message: "Keyword Updated",
      });
      updateKeywordState(page, rowsPerPage, listEvents, "edit");
    });
  };

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (e) => {
    if (!e.target.checked) return setSelected([]);

    const newSelected = keywords.map((n) => n.keywordID);

    setSelected(newSelected);
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (_, newPage) => {
    setKeywordRequestBody({ ...keywordRequestBody, page: newPage });

    updateKeywordState(newPage, rowsPerPage, listEvents, "pagination");
  };

  const handleChangeRowsPerPage = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);

    setKeywordRequestBody({
      ...keywordRequestBody,
      page: 0,
      rowsPerPage: newRowsPerPage,
    });

    updateKeywordState(0, newRowsPerPage, listEvents, "pagination");
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump
  const emptyRows = rowsPerPage - keywords.length;

  // Avoid a layout jump when storing previous data
  // const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - keywords.length) : 0;

  return (
    <>
      <Paper>
        <TableToolbar
          selected={selected}
          setSelected={setSelected}
          actions={actions}
          updateKeywordState={updateKeywordState}
          keywordRequestBody={keywordRequestBody}
          setKeywordRequestBody={setKeywordRequestBody}
          emitSnack={emitSnack}
        />

        <TableContainer>
          <Table stickyHeader>
            <TableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={keywords.length}
            />
            <TableBody>
              {stableSort(keywords, getComparator(order, orderBy)).map(
                (keyword, i) => {
                  const events = keyword.events || [];
                  const isItemSelected = isSelected(keyword.keywordID);

                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={i}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={() => handleClick(keyword.keywordID)}
                        />
                      </TableCell>
                      <TableCell scope="row" padding="none">
                        {keyword.keyword}
                      </TableCell>

                      {listEvents && events.length > 0 ? (
                        <TableCell>
                          {<ListEventsModal events={events} />}
                        </TableCell>
                      ) : (
                        <TableCell>
                          {keyword.assocEventCount > 0
                            ? keyword.assocEventCount
                            : "No Associated Events"}
                        </TableCell>
                      )}

                      <TableCell padding="none">
                        {format(keyword.dateModified, "MM/DD/YYYY")}
                      </TableCell>
                      <TableCell padding="none">
                        {format(keyword.dateCreated, "MM/DD/YYYY")}
                      </TableCell>

                      <TableCell padding="none">
                        <Tooltip title="Edit Keyword">
                          <IconButton
                            onClick={() => {
                              setKeywordToEdit(keyword);
                              toggleEditKeywordModal();
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                }
              )}

              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 50.88 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <EditKeywordModal
        keywords={slimKeywords}
        open={editModalOpen}
        handleClose={toggleEditKeywordModal}
        keywordToEdit={keywordToEdit}
        handleSubmit={handleEditKeyword}
      />
    </>
  );
};

KeywordsTable.propTypes = {
  keywords: PropTypes.arrayOf(
    PropTypes.shape({
      keyword: PropTypes.string,
      keywordID: PropTypes.string,
    })
  ),
  actions: PropTypes.object.isRequired,
  credentials: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  keywordRequestBody: PropTypes.object.isRequired,
  setKeywordRequestBody: PropTypes.func.isRequired,
  updateKeywordState: PropTypes.func.isRequired,
  emitSnack: PropTypes.func.isRequired,
};

export default KeywordsTable;
